import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';
import {
  CreditCard,
  Smartphone,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Copy,
  Car,
  Building,
  Home,
  Plane,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Booking = () => {
  const { t, language, dir, getLocalizedField } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');
  const fromQuote = location.state?.fromQuote || null;

  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSubService, setSelectedSubService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [mobileMoneyInfo, setMobileMoneyInfo] = useState(null);
  const [ctpayInfo, setCtpayInfo] = useState(null);
  const [ctpayOperators, setCtpayOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [pollingStatus, setPollingStatus] = useState(false);

  const [formData, setFormData] = useState({
    event_type: '',
    guest_count: '',
    location: '',
    notes: '',
    contact_name: user?.name || '',
    contact_email: user?.email || '',
    contact_phone: user?.phone || '',
    budget_range: '',
    vehicle_type: '',
    venue_type: '',
    rental_duration_days: 1,
  });

  const locales = { fr, en: enUS, ar };

  useEffect(() => {
    fetchServices();
    fetchCtpayOperators();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        contact_name: user.name || prev.contact_name,
        contact_email: user.email || prev.contact_email,
        contact_phone: user.phone || prev.contact_phone,
      }));
    }
  }, [user]);

  useEffect(() => {
    if (preselectedService && services.length > 0) {
      const service = services.find((s) => s.id === preselectedService);
      if (service) {
        setSelectedService(service);
      }
    }
  }, [preselectedService, services]);

  // Pre-fill booking when coming from Quote Simulator
  useEffect(() => {
    if (fromQuote && services.length > 0) {
      const service = services.find((s) => s.id === fromQuote.serviceId);
      if (service) {
        setSelectedService(service);
        if (fromQuote.eventDate) {
          try { setSelectedDate(new Date(fromQuote.eventDate)); } catch {}
        }
        if (fromQuote.durationDays) {
          setFormData((p) => ({ ...p, rental_duration_days: fromQuote.durationDays }));
        }
        // Pre-fill notes with quote details
        const linesText = (fromQuote.lines || [])
          .map((l) => `- ${l.item_label} × ${l.quantity} (${l.unit})`)
          .join('\n');
        setFormData((p) => ({
          ...p,
          notes: `Devis ${fromQuote.quoteReference} (Total estimatif: ${new Intl.NumberFormat('fr-FR').format(fromQuote.total)} XAF)\n\nÉléments sélectionnés:\n${linesText}`,
          event_type: p.event_type || 'rental',
        }));
        // Skip sub-service picker if coming with a quote
        setStep(2);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromQuote, services]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchCtpayOperators = async () => {
    try {
      const response = await axios.get(`${API}/payments/ctpay/operators`);
      setCtpayOperators(response.data.operators || []);
    } catch (error) {
      console.error('Error fetching CT Pay operators:', error);
    }
  };

  const calculateTotalAmount = () => {
    if (fromQuote?.total) return fromQuote.total;
    if (!selectedService) return 0;
    
    let basePrice = selectedService.base_price;
    
    if (selectedSubService) {
      basePrice = selectedSubService.base_price || selectedService.base_price;
      
      // Multiply by duration for per_day or per_night pricing
      if (selectedSubService.price_unit === 'per_day' || selectedSubService.price_unit === 'per_night') {
        basePrice *= formData.rental_duration_days || 1;
      }
    }
    
    return basePrice;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService || !selectedDate) {
      toast.error('Veuillez sélectionner un service et une date');
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        service_id: selectedService.id,
        sub_service_type: selectedSubService?.type || null,
        event_date: format(selectedDate, 'yyyy-MM-dd'),
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
        ...formData,
        guest_count: formData.guest_count ? parseInt(formData.guest_count) : null,
        rental_duration_days: formData.rental_duration_days || null,
      };

      const response = await axios.post(`${API}/bookings`, bookingData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      setBooking(response.data);
      setStep(3);
      toast.success(t('booking.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    if (!booking) return;
    setLoading(true);

    try {
      const totalAmount = calculateTotalAmount();
      const response = await axios.post(`${API}/payments/stripe/create-session`, {
        booking_id: booking.id,
        amount: totalAmount / 655, // Convert XAF to USD approx
        payment_method: 'stripe',
        currency: 'USD',
      }, {
        headers: { origin: window.location.origin },
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Erreur lors du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileMoneyPayment = async (provider) => {
    if (!booking) return;
    setLoading(true);

    try {
      const totalAmount = calculateTotalAmount();
      const response = await axios.post(`${API}/payments/mobile-money/initiate`, {
        booking_id: booking.id,
        phone_number: formData.contact_phone,
        provider,
        amount: totalAmount,
      });

      setMobileMoneyInfo(response.data);
      setPaymentMethod(provider);
    } catch (error) {
      toast.error('Erreur lors de l\'initiation du paiement');
    } finally {
      setLoading(false);
    }
  };

  const handleCtpayPayment = async () => {
    if (!booking || !selectedOperator) {
      toast.error('Veuillez sélectionner un opérateur');
      return;
    }
    setLoading(true);

    try {
      const totalAmount = calculateTotalAmount();
      const response = await axios.post(`${API}/payments/ctpay/initiate`, {
        booking_id: booking.id,
        phone_number: formData.contact_phone,
        operator_key: selectedOperator,
        amount: Math.round(totalAmount),
      });

      setCtpayInfo(response.data);
      setPaymentMethod('ctpay');
      
      // Start polling for payment status
      if (response.data.process_code) {
        pollCtpayStatus(response.data.process_code);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement CT Pay');
    } finally {
      setLoading(false);
    }
  };

  const pollCtpayStatus = async (processCode) => {
    setPollingStatus(true);
    let attempts = 0;
    const maxAttempts = 30; // 5 minutes max
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/ctpay/status/${processCode}`);
        
        if (response.data.status === 'success') {
          setPollingStatus(false);
          toast.success('Paiement confirmé !');
          // Refresh booking
          const bookingRes = await axios.get(`${API}/bookings/${booking.id}`);
          setBooking(bookingRes.data);
          return;
        } else if (response.data.status === 'failed' || response.data.status === 'cancelled') {
          setPollingStatus(false);
          toast.error('Paiement échoué ou annulé');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 10000); // Check every 10 seconds
        } else {
          setPollingStatus(false);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 10000);
        }
      }
    };
    
    checkStatus();
  };

  const copyReference = (ref) => {
    navigator.clipboard.writeText(ref);
    toast.success('Référence copiée');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const eventTypes = [
    { value: 'wedding', label: { fr: 'Mariage', en: 'Wedding', ar: 'زفاف' } },
    { value: 'corporate', label: { fr: 'Événement Corporate', en: 'Corporate Event', ar: 'فعالية مؤسسية' } },
    { value: 'birthday', label: { fr: 'Anniversaire', en: 'Birthday', ar: 'عيد ميلاد' } },
    { value: 'gala', label: { fr: 'Gala', en: 'Gala', ar: 'حفل' } },
    { value: 'retreat', label: { fr: 'Retraite', en: 'Retreat', ar: 'خلوة' } },
    { value: 'rental', label: { fr: 'Location', en: 'Rental', ar: 'تأجير' } },
    { value: 'other', label: { fr: 'Autre', en: 'Other', ar: 'آخر' } },
  ];

  const budgetRanges = [
    { value: '100000-500000', label: '100K - 500K XAF' },
    { value: '500000-1000000', label: '500K - 1M XAF' },
    { value: '1000000-2500000', label: '1M - 2.5M XAF' },
    { value: '2500000-5000000', label: '2.5M - 5M XAF' },
    { value: '5000000+', label: '5M+ XAF' },
  ];

  const subServiceIcons = {
    vehicle_rental: Car,
    venue_rental: Building,
    accommodation: Home,
    airport_vip: Plane,
  };

  return (
    <div className="min-h-screen bg-background-light pt-24 pb-16" dir={dir}>
      <div className="max-w-4xl mx-auto px-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 flex items-center justify-center font-body font-medium ${
                  step >= s
                    ? 'bg-primary text-white'
                    : 'bg-surface-light text-text-muted border border-primary/20'
                }`}
              >
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`w-16 md:w-24 h-0.5 ${
                    step > s ? 'bg-primary' : 'bg-primary/20'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Service & Date */}
        {step === 1 && (
          <div className="animate-fade-in" data-testid="booking-step-1">
            <h1 className="font-heading text-3xl font-light text-text-primary mb-8 text-center">
              {t('booking.selectService')}
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => {
                    setSelectedService(service);
                    setSelectedSubService(null);
                  }}
                  className={`text-left p-4 border transition-all ${
                    selectedService?.id === service.id
                      ? 'border-primary bg-primary/5'
                      : 'border-primary/20 hover:border-primary/40'
                  }`}
                  data-testid={`select-service-${service.id}`}
                >
                  <img
                    src={service.image_url}
                    alt={getLocalizedField(service, 'title')}
                    className="w-full h-32 object-cover mb-3"
                  />
                  <p className="overline text-secondary text-xs mb-1">{service.category}</p>
                  <h3 className="font-heading text-lg font-medium text-text-primary mb-1">
                    {getLocalizedField(service, 'title')}
                  </h3>
                  <p className="font-body text-sm text-primary">
                    {language === 'ar' ? 'من' : 'À partir de'} {formatPrice(service.base_price)} XAF
                  </p>
                  {service.has_sub_services && (
                    <span className="inline-block mt-2 text-xs font-body text-secondary bg-secondary/10 px-2 py-1">
                      {language === 'ar' ? 'خدمات فرعية متاحة' : language === 'en' ? 'Sub-services available' : 'Sous-services disponibles'}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Sub-services for Concierge */}
            {selectedService?.has_sub_services && selectedService.sub_services && (
              <div className="bg-white p-6 border border-primary/10 shadow-lg mb-8">
                <h2 className="font-heading text-xl font-medium text-text-primary mb-4">
                  {language === 'ar' ? 'اختر نوع الخدمة' : language === 'en' ? 'Choose service type' : 'Choisissez le type de service'}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedService.sub_services.map((sub) => {
                    const Icon = subServiceIcons[sub.type] || Building;
                    return (
                      <button
                        key={sub.type}
                        onClick={() => setSelectedSubService(sub)}
                        className={`flex items-start gap-4 p-4 border transition-all text-left ${
                          selectedSubService?.type === sub.type
                            ? 'border-primary bg-primary/5'
                            : 'border-primary/20 hover:border-primary/40'
                        }`}
                        data-testid={`select-subservice-${sub.type}`}
                      >
                        <div className="p-3 bg-primary/10 rounded-full">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading text-base font-medium text-text-primary">
                            {getLocalizedField(sub, 'title')}
                          </h3>
                          <p className="font-body text-xs text-text-muted mt-1">
                            {getLocalizedField(sub, 'description')}
                          </p>
                          <p className="font-body text-sm text-primary mt-2">
                            {formatPrice(sub.base_price)} XAF / {
                              sub.price_unit === 'per_day' ? (language === 'ar' ? 'يوم' : 'jour') :
                              sub.price_unit === 'per_night' ? (language === 'ar' ? 'ليلة' : 'nuit') :
                              (language === 'ar' ? 'خدمة' : 'service')
                            }
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sub-service specific options */}
            {selectedSubService && (
              <div className="bg-white p-6 border border-primary/10 shadow-lg mb-8">
                {selectedSubService.type === 'vehicle_rental' && selectedSubService.options && (
                  <div>
                    <label className="block font-body text-sm text-text-muted mb-2">
                      {language === 'ar' ? 'نوع السيارة' : language === 'en' ? 'Vehicle type' : 'Type de véhicule'}
                    </label>
                    <select
                      value={formData.vehicle_type}
                      onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                      className="input-flushed w-full mb-4"
                    >
                      <option value="">--</option>
                      {selectedSubService.options.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedSubService.type === 'venue_rental' && selectedSubService.venue_types && (
                  <div>
                    <label className="block font-body text-sm text-text-muted mb-2">
                      {language === 'ar' ? 'نوع القاعة' : language === 'en' ? 'Venue type' : 'Type de salle'}
                    </label>
                    <select
                      value={formData.venue_type}
                      onChange={(e) => setFormData({ ...formData, venue_type: e.target.value })}
                      className="input-flushed w-full mb-4"
                    >
                      <option value="">--</option>
                      {selectedSubService.venue_types.map((vt) => (
                        <option key={vt.key} value={vt.key}>
                          {vt[`label_${language}`] || vt.label_fr}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(selectedSubService.price_unit === 'per_day' || selectedSubService.price_unit === 'per_night') && (
                  <div>
                    <label className="block font-body text-sm text-text-muted mb-2">
                      {language === 'ar' ? 'عدد الأيام' : language === 'en' ? 'Number of days' : 'Nombre de jours'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.rental_duration_days}
                      onChange={(e) => setFormData({ ...formData, rental_duration_days: parseInt(e.target.value) || 1 })}
                      className="input-flushed w-full"
                    />
                    <p className="font-body text-sm text-primary mt-2">
                      Total: {formatPrice(selectedSubService.base_price * (formData.rental_duration_days || 1))} XAF
                    </p>
                  </div>
                )}
              </div>
            )}

            {(selectedService && (!selectedService.has_sub_services || selectedSubService)) && (
              <div className="bg-white p-6 border border-primary/10 shadow-lg mb-8">
                <h2 className="font-heading text-xl font-medium text-text-primary mb-4">
                  {t('booking.selectDate')}
                </h2>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date()}
                  locale={locales[language] || locales.fr}
                  className="mx-auto"
                />
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={!selectedService || !selectedDate || (selectedService.has_sub_services && !selectedSubService)}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="booking-next-step-1"
              >
                {language === 'ar' ? 'التالي' : 'Suivant'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Event Details */}
        {step === 2 && (
          <div className="animate-fade-in" data-testid="booking-step-2">
            <h1 className="font-heading text-3xl font-light text-text-primary mb-8 text-center">
              {t('booking.title')}
            </h1>

            {/* Summary */}
            <div className="bg-surface-light p-4 mb-6 border border-primary/10">
              <p className="font-body text-sm text-text-muted">
                <strong>{getLocalizedField(selectedService, 'title')}</strong>
                {selectedSubService && ` - ${getLocalizedField(selectedSubService, 'title')}`}
              </p>
              <p className="font-body text-sm text-text-muted">
                {format(selectedDate, 'dd MMMM yyyy', { locale: locales[language] || locales.fr })}
                {formData.rental_duration_days > 1 && ` (${formData.rental_duration_days} ${language === 'ar' ? 'أيام' : 'jours'})`}
              </p>
              <p className="font-heading text-xl text-primary mt-2">
                {formatPrice(calculateTotalAmount())} XAF
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 border border-primary/10 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.eventType')} *
                  </label>
                  <select
                    value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                    required
                    className="input-flushed w-full"
                    data-testid="booking-event-type"
                  >
                    <option value="">--</option>
                    {eventTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label[language] || type.label.en}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.guestCount')}
                  </label>
                  <input
                    type="number"
                    value={formData.guest_count}
                    onChange={(e) => setFormData({ ...formData, guest_count: e.target.value })}
                    className="input-flushed w-full"
                    data-testid="booking-guest-count"
                  />
                </div>

                <div>
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.location')} *
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    className="input-flushed w-full"
                    placeholder="Douala, Yaoundé..."
                    data-testid="booking-location"
                  />
                </div>

                <div>
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.budgetRange')}
                  </label>
                  <select
                    value={formData.budget_range}
                    onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                    className="input-flushed w-full"
                    data-testid="booking-budget"
                  >
                    <option value="">--</option>
                    {budgetRanges.map((range) => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.notes')}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="input-flushed w-full resize-none"
                    placeholder={language === 'ar' ? 'أي تفاصيل إضافية...' : 'Détails supplémentaires, demandes spéciales...'}
                    data-testid="booking-notes"
                  />
                </div>

                <div className="md:col-span-2 border-t border-primary/10 pt-6 mt-4">
                  <h3 className="font-heading text-lg font-medium text-text-primary mb-4">
                    {language === 'ar' ? 'معلومات الاتصال' : 'Informations de contact'}
                  </h3>
                </div>

                <div>
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.contactName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    required
                    className="input-flushed w-full"
                    data-testid="booking-contact-name"
                  />
                </div>

                <div>
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.contactEmail')} *
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    required
                    className="input-flushed w-full"
                    data-testid="booking-contact-email"
                  />
                </div>

                <div>
                  <label className="block font-body text-sm text-text-muted mb-2">
                    {t('booking.contactPhone')} *
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    required
                    placeholder="+237..."
                    className="input-flushed w-full"
                    data-testid="booking-contact-phone"
                  />
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="btn-outline flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {language === 'ar' ? 'السابق' : 'Précédent'}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="booking-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {t('booking.submit')}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && booking && (
          <div className="animate-fade-in" data-testid="booking-step-3">
            <div className="text-center mb-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="font-heading text-3xl font-light text-text-primary mb-2">
                {t('booking.success')}
              </h1>
              <p className="font-body text-text-muted">
                Référence: <span className="text-primary font-medium">{booking.reference}</span>
              </p>
            </div>

            <div className="bg-white p-8 border border-primary/10 shadow-lg mb-8">
              <h2 className="font-heading text-xl font-medium text-text-primary mb-6">
                {t('payment.title')}
              </h2>

              <div className="bg-surface-light p-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-body text-text-muted">Total</span>
                  <span className="font-heading text-2xl text-primary">
                    {formatPrice(calculateTotalAmount())} XAF
                  </span>
                </div>
              </div>

              {!mobileMoneyInfo && !ctpayInfo ? (
                <>
                  <p className="font-body text-sm text-text-muted mb-4">
                    {t('payment.selectMethod')}
                  </p>

                  <div className="space-y-3">
                    {/* Stripe */}
                    <button
                      onClick={handleStripePayment}
                      disabled={loading}
                      className="w-full flex items-center gap-4 p-4 border border-primary/20 hover:border-primary hover:bg-primary/5 transition-all"
                      data-testid="payment-stripe"
                    >
                      <CreditCard className="w-6 h-6 text-primary" />
                      <span className="font-body font-medium">{t('payment.stripe')}</span>
                    </button>

                    {/* CT Pay */}
                    <div className="border border-primary/20 p-4">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                          <span className="text-white text-xs font-bold">CT</span>
                        </div>
                        <span className="font-body font-medium">CT Pay (Mobile Money)</span>
                      </div>
                      
                      <div className="flex gap-2 mb-4">
                        {ctpayOperators.map((op) => (
                          <button
                            key={op.key}
                            onClick={() => setSelectedOperator(op.key)}
                            className={`flex-1 p-3 border text-center transition-all ${
                              selectedOperator === op.key
                                ? 'border-primary bg-primary/10'
                                : 'border-primary/20 hover:border-primary/40'
                            }`}
                          >
                            <span className={`inline-block w-8 h-8 rounded-full mb-1 ${
                              op.key === 'MOMO' ? 'bg-yellow-400' : 'bg-orange-500'
                            }`} />
                            <p className="font-body text-xs">{op.name}</p>
                          </button>
                        ))}
                      </div>
                      
                      <button
                        onClick={handleCtpayPayment}
                        disabled={loading || !selectedOperator}
                        className="w-full btn-primary disabled:opacity-50"
                        data-testid="payment-ctpay"
                      >
                        {loading ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Traitement...
                          </span>
                        ) : (
                          'Payer avec CT Pay'
                        )}
                      </button>
                    </div>

                    {/* Manual Mobile Money */}
                    <div className="border-t border-primary/10 pt-4 mt-4">
                      <p className="font-body text-xs text-text-muted mb-3">
                        {language === 'ar' ? 'أو الدفع يدوياً:' : 'Ou paiement manuel:'}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleMobileMoneyPayment('mtn')}
                          disabled={loading}
                          className="flex-1 flex items-center justify-center gap-2 p-3 border border-primary/20 hover:border-primary hover:bg-primary/5 transition-all"
                          data-testid="payment-mtn"
                        >
                          <div className="w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                            <Smartphone className="w-4 h-4 text-black" />
                          </div>
                          <span className="font-body text-sm">MTN MoMo</span>
                        </button>

                        <button
                          onClick={() => handleMobileMoneyPayment('orange')}
                          disabled={loading}
                          className="flex-1 flex items-center justify-center gap-2 p-3 border border-primary/20 hover:border-primary hover:bg-primary/5 transition-all"
                          data-testid="payment-orange"
                        >
                          <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                            <Smartphone className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-body text-sm">Orange</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : ctpayInfo ? (
                <div className="bg-surface-light p-6" data-testid="ctpay-instructions">
                  <h3 className="font-heading text-lg font-medium text-text-primary mb-4">
                    {ctpayInfo.success ? 'Paiement en cours' : 'Erreur'}
                  </h3>

                  {pollingStatus && (
                    <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 border border-blue-200">
                      <div className="w-5 h-5 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                      <span className="font-body text-sm text-blue-700">
                        En attente de confirmation sur votre téléphone...
                      </span>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <p className="font-body text-sm text-text-muted mb-1">{t('payment.reference')}</p>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-lg text-primary bg-white px-4 py-2 flex-1">
                          {ctpayInfo.reference}
                        </code>
                        <button
                          onClick={() => copyReference(ctpayInfo.reference)}
                          className="p-2 hover:bg-white transition-colors"
                        >
                          <Copy className="w-5 h-5 text-text-muted" />
                        </button>
                      </div>
                    </div>

                    <p className="font-body text-text-primary leading-relaxed">
                      {ctpayInfo.instructions?.[language] || ctpayInfo.instructions?.fr || ctpayInfo.message}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-surface-light p-6" data-testid="mobile-money-instructions">
                  <h3 className="font-heading text-lg font-medium text-text-primary mb-4">
                    {t('payment.instructions')}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <p className="font-body text-sm text-text-muted mb-1">{t('payment.reference')}</p>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-lg text-primary bg-white px-4 py-2 flex-1">
                          {mobileMoneyInfo.reference}
                        </code>
                        <button
                          onClick={() => copyReference(mobileMoneyInfo.reference)}
                          className="p-2 hover:bg-white transition-colors"
                        >
                          <Copy className="w-5 h-5 text-text-muted" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="font-body text-sm text-text-muted mb-1">Code USSD</p>
                      <code className="font-mono text-lg text-secondary">
                        {mobileMoneyInfo.ussd_code}
                      </code>
                    </div>

                    <p className="font-body text-text-primary leading-relaxed">
                      {mobileMoneyInfo.instructions[language] || mobileMoneyInfo.instructions.fr}
                    </p>

                    <div className="pt-4 border-t border-primary/10">
                      <p className="font-body text-sm text-text-muted">
                        Montant: <span className="text-primary font-medium">{formatPrice(mobileMoneyInfo.amount)} XAF</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate(user ? '/dashboard' : '/')}
                className="btn-outline"
              >
                {user ? t('nav.dashboard') : t('nav.home')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Booking;
